/**
 * Mana Locals Admin — the assistant's ENVELOPES.
 *
 * CONTRACT SOURCE: the M71 assistant contract published by the API repository. Every
 * assistant answer arrives in the API's correlation envelope `{ data, meta }`, and
 * `src/api/client/http.ts` parses the RAW body — so a schema that described only the
 * payload would reject the API's own answer, and the console would report a contract
 * mismatch for a response that was perfectly correct.
 *
 * These wrappers are the boundary. The payload shapes live in `assistant.ts`; the
 * service unwraps `data` and hands the screens the payload.
 *
 * The `meta` half is the API's `MetaSchema` (`{ requestId }`), the same one every
 * other module carries.
 */
import { z } from 'zod';
import { ResponseMetaSchema } from '@/api/schemas/common';
import {
  AssistantContextSchema,
  AssistantMessageSchema,
  AssistantThreadSchema,
  AssistantTurnSchema,
} from '@/api/schemas/assistant';

/** The route-aware context, in its envelope. */
export const AssistantContextResponseSchema = z.object({
  data: AssistantContextSchema,
  meta: ResponseMetaSchema,
});

export type AssistantContextResponse = z.infer<typeof AssistantContextResponseSchema>;

/** One conversation, in its envelope. */
export const AssistantThreadResponseSchema = z.object({
  data: AssistantThreadSchema,
  meta: ResponseMetaSchema,
});

export type AssistantThreadResponse = z.infer<typeof AssistantThreadResponseSchema>;

/** The caller's conversations, in their envelope. */
export const AssistantThreadListResponseSchema = z.object({
  data: z.object({ items: z.array(AssistantThreadSchema) }),
  meta: ResponseMetaSchema,
});

export type AssistantThreadListResponse = z.infer<typeof AssistantThreadListResponseSchema>;

/** One conversation's turns, in their envelope. */
export const AssistantMessageListResponseSchema = z.object({
  data: z.object({ items: z.array(AssistantMessageSchema) }),
  meta: ResponseMetaSchema,
});

export type AssistantMessageListResponse = z.infer<typeof AssistantMessageListResponseSchema>;

/** One turn, in its envelope. */
export const AssistantTurnResponseSchema = z.object({
  data: AssistantTurnSchema,
  meta: ResponseMetaSchema,
});

export type AssistantTurnResponse = z.infer<typeof AssistantTurnResponseSchema>;
